export interface DocumentGuide {
  id: string;
  name: string;
  badge: string;
  iconName: string;
  officialAgency: string;
  agencyWebsite: string;
  agencyPhone: string;
  sensitiveElements: {
    title: string;
    description: string;
    riskLevel: 'high' | 'critical';
  }[];
  whatToDoIfCompromised: string[];
  replacementProcess: {
    step: number;
    title: string;
    details: string;
  }[];
  stateSpecificRules?: {
    state: string;
    authority: string;
    reissueNotes: string;
    contactUrl: string;
  }[];
  futurePreventionTips: string[];
}

export const AUSTRALIAN_DOCUMENTS: DocumentGuide[] = [
  {
    id: 'driver-licence',
    name: 'Driver Licence (State & Territory)',
    badge: 'Primary ID Document',
    iconName: 'CreditCard',
    officialAgency: 'State & Territory Road Transport Authorities / DVS',
    agencyWebsite: 'https://www.infrastructure.gov.au',
    agencyPhone: 'Contact your state service centre',
    sensitiveElements: [
      {
        title: 'Licence Number & Card Number',
        description: 'Australia’s Document Verification Service (DVS) matches both the Licence Number AND the physical Card Number (which changes on replacement). Compromising both enables unauthorized online identity verification.',
        riskLevel: 'critical'
      },
      {
        title: 'Full Legal Name & Date of Birth',
        description: 'Used across all 100-point identification checks including bank accounts and mobile plans.',
        riskLevel: 'high'
      },
      {
        title: 'Residential Address & Signature',
        description: 'Can be used for credential stuffing or fake tenancy and loan applications.',
        riskLevel: 'high'
      }
    ],
    whatToDoIfCompromised: [
      'Immediately contact your State Road Transport Authority (e.g. Service NSW, VicRoads, QLD Transport) to request a new physical card with a new Card Number.',
      'Notify IDCARE (1800 595 160) for free case management and support.',
      'Place an immediate temporary ban on your credit report with Equifax, Experian, and illion.',
      'Ask your state road authority to flag your licence record for enhanced security questions.',
      'Keep copies of police event numbers or reference numbers from your fraud report.'
    ],
    replacementProcess: [
      {
        step: 1,
        title: 'Request Card Number Reissue',
        details: 'Apply online or at a service centre for a replacement licence card. Note that your Licence Number may remain the same, but the unique Card Number printed on the card will change, which invalidates the old card for DVS checks.'
      },
      {
        step: 2,
        title: 'Request Full Licence Number Change (If Severe Fraud)',
        details: 'If someone is actively taking loans in your name, state authorities can issue a completely new Licence Number with police report evidence (fee is typically waived for verified fraud victims).'
      },
      {
        step: 3,
        title: 'Update Digital Licences',
        details: 'Refresh your digital licence app (e.g. Service NSW app, Service Victoria app) to ensure the newly issued card number syncs.'
      }
    ],
    stateSpecificRules: [
      {
        state: 'NSW',
        authority: 'Service NSW',
        reissueNotes: 'Card number changes immediately upon ordering replacement. Licence number change requires police event number.',
        contactUrl: 'https://www.service.nsw.gov.au'
      },
      {
        state: 'VIC',
        authority: 'VicRoads',
        reissueNotes: 'Replacement card invalidates previous card number on DVS. Support available via VicRoads Identity Security Team.',
        contactUrl: 'https://www.vicroads.vic.gov.au'
      },
      {
        state: 'QLD',
        authority: 'Queensland Department of Transport and Main Roads',
        reissueNotes: 'Replacement card generates new card number. In-person visit required for full licence number replacement.',
        contactUrl: 'https://www.qld.gov.au/transport'
      },
      {
        state: 'WA',
        authority: 'Department of Transport (DoT) WA',
        reissueNotes: 'Card number is updated on physical replacement. Fraud report enables priority support.',
        contactUrl: 'https://www.transport.wa.gov.au'
      },
      {
        state: 'SA',
        authority: 'Service SA',
        reissueNotes: 'New card number is generated upon card replacement. Digital wallet refreshes automatically.',
        contactUrl: 'https://www.sa.gov.au'
      }
    ],
    futurePreventionTips: [
      'Never send an un-watermarked photo of your driver licence for rental applications, hotel check-ins, or marketplace trades.',
      'Use the IDFraud.au Watermark tool before sharing any digital copy.',
      'Keep your physical card in an RFID-blocking wallet.'
    ]
  },
  {
    id: 'medicare',
    name: 'Medicare Card',
    badge: 'National Health & ID Document',
    iconName: 'HeartPulse',
    officialAgency: 'Services Australia',
    agencyWebsite: 'https://www.servicesaustralia.gov.au/medicare',
    agencyPhone: '132 011 (Medicare General Enquiries)',
    sensitiveElements: [
      {
        title: 'Medicare Number & Individual Reference Number (IRN)',
        description: 'The combination of your 10-digit Medicare number, your position number (1-9), and the card expiry date forms a Tier-2 identity verification point in Australia.',
        riskLevel: 'critical'
      }
    ],
    whatToDoIfCompromised: [
      'Sign in to your myGov account linked to Medicare and request a replacement card with a new card number.',
      'Call the Services Australia Scams and Identity Theft Helpdesk on 1800 941 126.',
      'Contact IDCARE (1800 595 160) if your Medicare card was part of a major data breach.',
      'Review your Medicare claim history on myGov to confirm no unauthorized medical claims or prescription benefits were claimed in your name.'
    ],
    replacementProcess: [
      {
        step: 1,
        title: 'Request New Card on myGov',
        details: 'Go to Medicare online account or Express Plus Medicare mobile app > Select "Replace a card" > Choose "I believe my card is compromised/lost".'
      },
      {
        step: 2,
        title: 'Receive New Medicare Number',
        details: 'Services Australia will issue a brand new Medicare card with a new 10-digit number and expiry date, rendering the old number invalid for DVS checks.'
      },
      {
        step: 3,
        title: 'Notify Your Doctors & Pharmacists',
        details: 'Update your GP clinic and regular pharmacies with your new card details at your next visit.'
      }
    ],
    futurePreventionTips: [
      'Do not allow businesses to scan or photocopy your Medicare card unless legally required for medical care or bulk billing.',
      'Do not carry spare old Medicare cards in your wallet.'
    ]
  },
  {
    id: 'passport',
    name: 'Australian Passport',
    badge: 'International Travel & Tier 1 ID',
    iconName: 'Globe',
    officialAgency: 'Australian Passport Office (DFAT)',
    agencyWebsite: 'https://www.passports.gov.au',
    agencyPhone: '131 232 (Australian Passport Information Service)',
    sensitiveElements: [
      {
        title: 'Passport Number & Machine Readable Zone (MRZ)',
        description: 'Highest-tier identification document in Australia and worldwide. Enables opening international bank accounts and border crossing verification.',
        riskLevel: 'critical'
      }
    ],
    whatToDoIfCompromised: [
      'Report the passport as lost or stolen immediately via the Australian Passport Office website or by phone on 131 232.',
      'Once reported, the passport is permanently cancelled immediately to prevent criminal misuse and cannot be reactivated.',
      'Obtain a police report reference number if your passport was stolen or involved in a break-in.'
    ],
    replacementProcess: [
      {
        step: 1,
        title: 'Cancellation',
        details: 'Log into Passports portal and report the document. It is instantly blacklisted on Interpol and DFAT databases.'
      },
      {
        step: 2,
        title: 'Apply for Replacement',
        details: 'Submit an application for a replacement passport through Australia Post with new passport photos and guarantor signature.'
      }
    ],
    futurePreventionTips: [
      'Store your physical passport in a home safe when not travelling.',
      'Never email clear, un-watermarked scans of your passport page.'
    ]
  },
  {
    id: 'tfn',
    name: 'Tax File Number (TFN)',
    badge: 'Confidential Tax ID',
    iconName: 'FileSpreadsheet',
    officialAgency: 'Australian Taxation Office (ATO)',
    agencyWebsite: 'https://www.ato.gov.au',
    agencyPhone: '1800 467 033 (ATO Client Identity Support Centre)',
    sensitiveElements: [
      {
        title: '9-Digit Tax File Number',
        description: 'Your TFN is for life. Compromised TFNs can be used to lodge fraudulent tax returns, redirect refund payments, or access superannuation funds.',
        riskLevel: 'critical'
      }
    ],
    whatToDoIfCompromised: [
      'Call the ATO Client Identity Support Centre immediately on 1800 467 033 (8:00am–6:00pm AEST, Mon–Fri).',
      'The ATO will place enhanced security warnings and two-step verification safeguards on your tax record.',
      'Log into myGov and inspect your recent tax lodgements, superannuation accounts, and linked bank accounts for unauthorized changes.'
    ],
    replacementProcess: [
      {
        step: 1,
        title: 'ATO Security Lock',
        details: 'The ATO applies an identity security lock to your profile. Any future interaction with the ATO will require strict identity authentication.'
      },
      {
        step: 2,
        title: 'TFN Reissue (Rare Circumstances)',
        details: 'In extreme cases of ongoing fraudulent tax activity, the ATO Commissioner may authorize the creation of a new replacement TFN.'
      }
    ],
    futurePreventionTips: [
      'Only provide your TFN to authorized entities: your employer, super fund, banks, tax agent, or Services Australia. Never provide a TFN for job applications prior to an actual job offer.'
    ]
  },
  {
    id: 'birth-certificate',
    name: 'Birth Certificate',
    badge: 'Foundation Identity Record',
    iconName: 'FileText',
    officialAgency: 'Registry of Births, Deaths & Marriages (State/Territory)',
    agencyWebsite: 'https://www.bdm.nsw.gov.au',
    agencyPhone: 'Contact your state BDM registry',
    sensitiveElements: [
      {
        title: 'Registration Number & Parents Information',
        description: 'Acts as foundational root document for obtaining passports, citizenship certificates, and legal name changes.',
        riskLevel: 'critical'
      }
    ],
    whatToDoIfCompromised: [
      'Contact your state BDM registry to alert them that your certificate was compromised.',
      'Order a replacement certificate with updated security features and registration certificate number.',
      'Report the loss to IDCARE and police.'
    ],
    replacementProcess: [
      {
        step: 1,
        title: 'Contact State BDM Registry',
        details: 'Provide primary identification and reason for replacement (theft/loss).'
      },
      {
        step: 2,
        title: 'Issue Replacement Certificate',
        details: 'A new physical certificate with modern holographic anti-fraud security foils is issued.'
      }
    ],
    futurePreventionTips: [
      'Keep original birth certificates locked in a fireproof secure box. Never carry it in day-to-day bags.'
    ]
  },
  {
    id: 'bank-cards',
    name: 'Australian Bank & Credit Cards',
    badge: 'Payment & Financial Access',
    iconName: 'CreditCard',
    officialAgency: 'Your Australian Financial Institution / AFCA',
    agencyWebsite: 'https://www.afca.org.au',
    agencyPhone: 'Call the 24/7 emergency fraud number on back of card',
    sensitiveElements: [
      {
        title: '16-Digit Card Number, Expiry & CVV',
        description: 'Enables fraudulent CNP (card not present) online transactions and unauthorized subscription billing.',
        riskLevel: 'critical'
      }
    ],
    whatToDoIfCompromised: [
      'Use your banking app immediately to temporarily freeze or lock the card.',
      'Call your bank’s 24/7 fraud hotline to cancel the card and dispute any unauthorized pending transactions under the ePayments Code.',
      'Review your account statements for small authorization testing charges (e.g. $1 or $0.50 charges).'
    ],
    replacementProcess: [
      {
        step: 1,
        title: 'Instant In-App Cancellation',
        details: 'Your bank cancels the existing card token and card number immediately.'
      },
      {
        step: 2,
        title: 'Digital Card Reissue',
        details: 'Most Australian banks (CommBank, NAB, ANZ, Westpac, Macquarie, etc.) issue a virtual card instantly to Apple Pay / Google Wallet while the plastic card is posted.'
      }
    ],
    futurePreventionTips: [
      'Enable instant push notifications for all card transactions in your banking app.',
      'Use dynamic CVV or single-use virtual cards for new online shopping sites.'
    ]
  }
];
